import { useState, useRef } from "react";
import {
  Box,
  Card,
  Typography,
  TextField,
  Button,
  Avatar,
  InputAdornment,
  IconButton,
  Divider,
} from "@mui/material";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Actions } from "../../redux/Actions/Actions";
import { endPoints } from "../../constant/Environment";
import { addData } from "../../Utility/API";
import Toaster from "../../components/Toast";
import { ErrorToast } from "../../components/Toast/message";
import Loader from "../../components/Loader";
import { tokens } from "../../theme";

// Temporary: this account skips the OTP step and logs in straight from /login.
const OTP_BYPASS_USER_ID = 20831;

const Login = () => {
  const recaptchaRef = useRef(null);
  const [, setRecaptchaToken] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordShow, setPasswordShow] = useState(false);
  const [isLoading, setIsLoadinig] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!email || !password) {
      ErrorToast("Please enter email and password.");
      return;
    }

    const data = { login: email, passwd: password };
    const url = `${endPoints.api.LOGIN_ENDPOINT}`;
    setIsLoadinig(true);
    await addData(url, data)
      .then((response) => {
        setIsLoadinig(false);
        if (response.data.status === "success") {
          setRecaptchaToken(null);
          recaptchaRef.current?.reset();

          if (Number(response.data.data?.idUser) === OTP_BYPASS_USER_ID) {
            // Bypass: complete the login here instead of going through OTP.
            const userloginData = response.data;
            sessionStorage.setItem("user", JSON.stringify(userloginData));
            sessionStorage.setItem(
              "session",
              JSON.stringify(userloginData.data.sessionId)
            );
            dispatch({ type: Actions.LOGIN, data: { user: userloginData } });
            navigate("/Home");
            return;
          }

          // Two-step auth: go to OTP verification with the login response.
          navigate("/verify-otp", { state: { loginData: response.data } });
        } else {
          ErrorToast(response.data.message);
        }
      })
      .catch((error) => {
        setIsLoadinig(false);
        console.error(error);
      });
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Toaster />
      {isLoading && <Loader spinner={true} visible={isLoading} />}

      <Card
        sx={{
          width: "100%",
          maxWidth: 420,
          p: { xs: 3, sm: 4.5 },
          borderRadius: 4,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Avatar
            variant="rounded"
            sx={{
              width: 56,
              height: 56,
              mb: 1.5,
              borderRadius: "14px",
              background: `linear-gradient(135deg, ${tokens.primary}, #5B3FB0)`,
              fontFamily: '"Space Grotesk", sans-serif',
              fontWeight: 700,
              fontSize: 28,
              color: "#fff",
              boxShadow: tokens.shadowPrimary,
            }}
          >
            S
          </Avatar>
          <Typography variant="h5">SpinSaga Admin</Typography>
          <Typography variant="body2" color="text.secondary">
            Sign in to your control panel
          </Typography>
        </Box>

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            fullWidth
            label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            margin="normal"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailRoundedIcon
                    fontSize="small"
                    sx={{ color: "text.secondary" }}
                  />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            label="Password"
            type={passwordShow ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="normal"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockRoundedIcon
                    fontSize="small"
                    sx={{ color: "text.secondary" }}
                  />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setPasswordShow((s) => !s)}
                    edge="end"
                    size="small"
                  >
                    {passwordShow ? (
                      <VisibilityOffRoundedIcon />
                    ) : (
                      <VisibilityRoundedIcon />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={isLoading}
            sx={{ mt: 3 }}
          >
            {isLoading ? "Signing in…" : "Sign In"}
          </Button>

          <Divider sx={{ my: 2.5, borderColor: tokens.border }}>
            <Typography variant="caption" color="text.secondary">
              secured access
            </Typography>
          </Divider>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", textAlign: "center" }}
          >
            Protected by session validation &amp; device checks
          </Typography>
        </Box>
      </Card>
    </Box>
  );
};

export default Login;
